/* src/components/pdf-converter.ts
 * Inline PDF conversion utilities — ported from pdf-tools/src/services/pdf-service.ts
 * Uses globally-loaded CDN libraries: window.jspdf (jsPDF), PDFLib, pdfjsLib
 */

declare const PDFLib: any;
declare const pdfjsLib: any;

export type ProgressFn = (percent: number, text: string) => void;

function dlFile(url: string, filename: string): void {
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
}

function imgToPdfBlob(file: File, imageType: string, jsPDF: any): Promise<Blob> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const imgData = e.target?.result as string;
                const doc = new jsPDF();
                const img = new Image();
                img.onload = () => {
                    const pw = doc.internal.pageSize.getWidth();
                    const ph = (img.height * pw) / img.width;
                    doc.addImage(imgData, imageType, 0, 0, pw, ph);
                    resolve(doc.output('blob'));
                };
                img.onerror = reject;
                img.src = imgData;
            } catch (err) { reject(err); }
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

export async function processPngToPdf(files: File[], progress: ProgressFn): Promise<void> {
    const { jsPDF } = (window as any).jspdf;
    for (let i = 0; i < files.length; i++) {
        progress(Math.round((i / files.length) * 100), `Converting ${i + 1} of ${files.length}…`);
        const blob = await imgToPdfBlob(files[i], 'PNG', jsPDF);
        const url = URL.createObjectURL(blob);
        dlFile(url, files[i].name.replace(/\.(png|PNG)$/, '.pdf'));
        setTimeout(() => URL.revokeObjectURL(url), 1500);
    }
}

export async function processJpgToPdf(files: File[], progress: ProgressFn): Promise<void> {
    const { jsPDF } = (window as any).jspdf;
    for (let i = 0; i < files.length; i++) {
        progress(Math.round((i / files.length) * 100), `Converting ${i + 1} of ${files.length}…`);
        const blob = await imgToPdfBlob(files[i], 'JPEG', jsPDF);
        const url = URL.createObjectURL(blob);
        dlFile(url, files[i].name.replace(/\.(jpe?g|JPE?G)$/, '.pdf'));
        setTimeout(() => URL.revokeObjectURL(url), 1500);
    }
}

export async function processPdfToDoc(files: File[], progress: ProgressFn): Promise<void> {
    for (let i = 0; i < files.length; i++) {
        progress(Math.round((i / files.length) * 100), `Extracting ${i + 1} of ${files.length}…`);
        const ab = await files[i].arrayBuffer();
        const pdfDoc = await PDFLib.PDFDocument.load(ab);
        let text = `Document: ${files[i].name}\n${'─'.repeat(40)}\n\n`;
        pdfDoc.getPages().forEach((_: any, idx: number) => { text += `[Page ${idx + 1}]\n\n`; });
        const blob = new Blob([text], { type: 'application/msword' });
        const url = URL.createObjectURL(blob);
        dlFile(url, files[i].name.replace(/\.(pdf|PDF)$/, '.doc'));
        setTimeout(() => URL.revokeObjectURL(url), 1500);
    }
}

export async function processDocToPdf(files: File[], progress: ProgressFn): Promise<void> {
    for (let i = 0; i < files.length; i++) {
        progress(Math.round((i / files.length) * 100), `Converting ${i + 1} of ${files.length}…`);
        const pdfDoc = await PDFLib.PDFDocument.create();
        const page = pdfDoc.addPage();
        page.drawText(`Converted from: ${files[i].name}`, { x: 50, y: page.getHeight() - 50, size: 12 });
        const bytes = await pdfDoc.save();
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        dlFile(url, files[i].name.replace(/\.(docx?)$/i, '.pdf'));
        setTimeout(() => URL.revokeObjectURL(url), 1500);
    }
}

export async function processPdfToJpg(files: File[], progress: ProgressFn): Promise<void> {
    for (let i = 0; i < files.length; i++) {
        const ab = await files[i].arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: ab }).promise;
        for (let p = 1; p <= pdf.numPages; p++) {
            progress(Math.round((p / pdf.numPages) * 100), `Page ${p} of ${pdf.numPages}…`);
            const page = await pdf.getPage(p);
            const vp = page.getViewport({ scale: 2 });
            const canvas = document.createElement('canvas');
            canvas.width = vp.width; canvas.height = vp.height;
            await page.render({ canvasContext: canvas.getContext('2d')!, viewport: vp }).promise;
            const blob = await new Promise<Blob>(res => canvas.toBlob(b => res(b!), 'image/jpeg', 0.92));
            const url = URL.createObjectURL(blob);
            dlFile(url, `${files[i].name.replace(/\.(pdf|PDF)$/, '')}_page_${p}.jpg`);
            setTimeout(() => URL.revokeObjectURL(url), 1500);
        }
    }
}

export async function processPdfToPng(files: File[], progress: ProgressFn): Promise<void> {
    for (let i = 0; i < files.length; i++) {
        const ab = await files[i].arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: ab }).promise;
        for (let p = 1; p <= pdf.numPages; p++) {
            progress(Math.round((p / pdf.numPages) * 100), `Page ${p} of ${pdf.numPages}…`);
            const page = await pdf.getPage(p);
            const vp = page.getViewport({ scale: 2 });
            const canvas = document.createElement('canvas');
            canvas.width = vp.width; canvas.height = vp.height;
            await page.render({ canvasContext: canvas.getContext('2d')!, viewport: vp }).promise;
            const blob = await new Promise<Blob>(res => canvas.toBlob(b => res(b!), 'image/png'));
            const url = URL.createObjectURL(blob);
            dlFile(url, `${files[i].name.replace(/\.(pdf|PDF)$/, '')}_page_${p}.png`);
            setTimeout(() => URL.revokeObjectURL(url), 1500);
        }
    }
}

export async function processCompressPdf(files: File[], progress: ProgressFn): Promise<void> {
    for (let i = 0; i < files.length; i++) {
        progress(Math.round((i / files.length) * 100), `Compressing ${i + 1} of ${files.length}…`);
        const ab = await files[i].arrayBuffer();
        const pdfDoc = await PDFLib.PDFDocument.load(ab);
        const bytes = await pdfDoc.save({ useObjectStreams: true });
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        dlFile(url, files[i].name.replace(/\.(pdf|PDF)$/, '_compressed.pdf'));
        setTimeout(() => URL.revokeObjectURL(url), 1500);
    }
}

export async function processCombinePdf(files: File[], progress: ProgressFn): Promise<void> {
    const merged = await PDFLib.PDFDocument.create();
    for (let i = 0; i < files.length; i++) {
        progress(Math.round(((i + 1) / files.length) * 100), `Merging ${i + 1} of ${files.length}…`);
        const ab = await files[i].arrayBuffer();
        const pdf = await PDFLib.PDFDocument.load(ab);
        const pages = await merged.copyPages(pdf, pdf.getPageIndices());
        pages.forEach((p: any) => merged.addPage(p));
    }
    const bytes = await merged.save();
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    dlFile(url, 'combined.pdf');
    setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export async function processCropPdf(files: File[], progress: ProgressFn): Promise<void> {
    progress(50, 'Cropping…');
    const ab = await files[0].arrayBuffer();
    const pdfDoc = await PDFLib.PDFDocument.load(ab);
    pdfDoc.getPages().forEach((p: any) => {
        const { width, height } = p.getSize();
        p.setCropBox(0, height * 0.25, width, height * 0.75);
    });
    const bytes = await pdfDoc.save();
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    dlFile(url, files[0].name.replace(/\.(pdf|PDF)$/, '_cropped.pdf'));
    setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export async function processStitch(
    files: File[],
    platform: 'ios' | 'android',
    theme: 'dark' | 'light',
    progress: ProgressFn
): Promise<void> {
    const STITCH_WORKER = localStorage.getItem('stitch_worker_url') || 'https://chat-stitch.kaywat.codes/extract';
    const RELAY_URL     = localStorage.getItem('stitch_relay_url')  || 'https://getavi-ai-models-relay.kaywat.codes';
    const RELAY_KEY     = localStorage.getItem('stitch_relay_key')  || 'Bearer avirelay_secure_2025';

    progress(10, 'Reading screenshots…');
    const images: string[] = await Promise.all(
        Array.from(files).map(f => new Promise<string>((res, rej) => {
            const r = new FileReader();
            r.onload = e => res(e.target?.result as string);
            r.onerror = rej;
            r.readAsDataURL(f);
        }))
    );

    progress(30, 'Sending to AI for OCR…');
    let result: any = null;

    try {
        const resp = await fetch(STITCH_WORKER, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ images, platform, theme }),
            signal: AbortSignal.timeout(60_000)
        });
        if (resp.ok) result = await resp.json();
    } catch {
        console.warn('[Stitch] Worker unavailable, falling back to relay vision…');
    }

    if (!result?.messages) {
        const content: any[] = [
            { type: 'text', text: `Extract these ${platform} Messages screenshots (${theme} mode). Return ONLY valid JSON: {"contactName":"string or null","messages":[{"sender":"me|them","text":"string","timestamp":"string or null"}]}` },
            ...images.map(url => ({ type: 'image_url', image_url: { url } }))
        ];
        const resp = await fetch(`${RELAY_URL}/v1/chat/completions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': RELAY_KEY },
            body: JSON.stringify({ model: 'gemini-2.0-flash', messages: [{ role: 'user', content }], stream: false }),
            signal: AbortSignal.timeout(90_000)
        });
        const data = await resp.json();
        const raw = data?.choices?.[0]?.message?.content ?? '';
        const m = raw.match(/\{[\s\S]*\}/);
        if (m) try { result = JSON.parse(m[0]); } catch { /* skip */ }
    }

    if (!result?.messages?.length) throw new Error('Could not extract messages from screenshots');

    progress(80, 'Building PDF transcript…');

    const { jsPDF } = (window as any).jspdf;
    const doc = new jsPDF();
    const contact = result.contactName || 'Unknown';
    let y = 20;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text(`Chat with: ${contact}`, 20, y);
    y += 8;
    doc.setDrawColor(168, 85, 247);
    doc.line(20, y, 190, y);
    y += 8;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);

    (result.messages as any[]).forEach((msg: any) => {
        const who = msg.sender === 'me' ? 'Me' : contact;
        const ts  = msg.timestamp ? ` [${msg.timestamp}]` : '';
        const line = `${who}${ts}: ${msg.text}`;
        const lines = doc.splitTextToSize(line, 170);
        if (y + lines.length * 5 > 285) { doc.addPage(); y = 15; }
        doc.setTextColor(msg.sender === 'me' ? '#a855f7' : '#cccccc');
        doc.text(lines, 20, y);
        y += lines.length * 5 + 2;
    });

    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);
    dlFile(url, `${contact.replace(/[^a-z0-9]/gi, '_')}_chat.pdf`);
    setTimeout(() => URL.revokeObjectURL(url), 1500);
}
